import React, { LabelHTMLAttributes, ReactElement, memo } from 'react';
import classNames from 'clsx';
import styles from './checkbox.component.module.scss';

enum AriaChecked {
  TRUE = 'true',
  FALSE = 'false',
  MIXED = 'mixed'
}

export interface Props {
  readonly id: string;
  readonly name: string;
  readonly value: string;
  readonly label: ReactElement | string;
  readonly labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
  readonly handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly checked?: boolean;
  readonly disabled?: boolean;
  readonly modified?: boolean;
  readonly ariaDescribedBy?: string;
}

function CheckboxComponent({
  id,
  name,
  value,
  label,
  labelProps,
  handleCheckboxChange,
  checked,
  disabled,
  modified,
  ariaDescribedBy
}: Props): ReactElement {
  const checkboxClassFactory = (
    modified: boolean | undefined,
    disabled: boolean | undefined
  ): string => {
    return classNames({
      [styles['checkbox-input']]: true,
      [styles['checkbox-input--modified']]: modified,
      [styles['checkbox-input--disabled']]: disabled
    });
  };

  let ariaChecked: AriaChecked = AriaChecked.FALSE;
  if (modified) {
    ariaChecked = AriaChecked.MIXED;
  } else if (checked) {
    ariaChecked = AriaChecked.TRUE;
  }

  return (
    <div
      className={classNames(styles['checkbox'], {
        [styles['checkbox--disabled']]: disabled
      })}
    >
      <input
        className={checkboxClassFactory(modified, disabled)}
        data-testid="checkbox"
        type="checkbox"
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={handleCheckboxChange}
        aria-checked={ariaChecked}
        aria-disabled={disabled}
        aria-describedby={ariaDescribedBy}
        tabIndex={disabled ? -1 : 0}
      />
      <label
        htmlFor={id}
        {...labelProps}
        className={classNames(styles['checkbox-label'], labelProps?.className)}
      >
        {label}
      </label>
    </div>
  );
}
export default memo(CheckboxComponent);
